import { useState } from "react";
import { getLocalObject } from "../../../common/utils";
import { ICart } from "../../../models";

const STORAGE_KEY = "bpb.admin.reservationContacts";

/** One person on the admin's temporary contact list, built on the Rezervacije page. */
export interface IReservationContact {
  name: string;
  email: string;
  phone: string;
  place: string;
  /** Reserved motif codes the person was added for. */
  motifs: string[];
  cartIds: number[];
}

const sameText = (a?: string, b?: string) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
const samePhone = (a?: string, b?: string) => {
  const digits = (s?: string) => (s || "").replace(/\D/g, "");
  return digits(a).length >= 6 && digits(a) === digits(b);
};

const save = (contacts: IReservationContact[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(contacts));
  } catch (e) {
    console.log(e);
  }
};

const load = (): IReservationContact[] => {
  try {
    const stored = getLocalObject(STORAGE_KEY);
    return Array.isArray(stored) ? stored : [];
  } catch (e) {
    return [];
  }
};

/**
 * Frontend-only list kept in localStorage until cleared. A person who reserved in several
 * orders (same email or phone) stays one row, with the motifs and orders merged.
 */
export const useContactList = () => {
  const [contacts, setContacts] = useState<IReservationContact[]>(load);

  const update = (next: IReservationContact[]) => {
    save(next);
    setContacts(next);
  };

  const isInList = (cartId: number) =>
    contacts.some((c) => c.cartIds.includes(cartId));

  const addMany = (orders: { cart: ICart; motifs: string[] }[]) => {
    const next = contacts.map((c) => ({ ...c }));
    orders.forEach(({ cart, motifs }) => {
      const existing = next.find(
        (c) =>
          c.cartIds.includes(cart.id) ||
          sameText(c.email, cart.email) ||
          samePhone(c.phone, cart.phone)
      );
      if (existing) {
        existing.motifs = Array.from(new Set([...existing.motifs, ...motifs]));
        existing.cartIds = Array.from(new Set([...existing.cartIds, cart.id]));
        existing.email = existing.email || cart.email || "";
        existing.phone = existing.phone || cart.phone || "";
      } else {
        next.push({
          name: cart.name || "",
          email: cart.email || "",
          phone: cart.phone || "",
          place: cart.place || "",
          motifs,
          cartIds: [cart.id],
        });
      }
    });
    update(next);
  };

  const add = (cart: ICart, motifs: string[]) => addMany([{ cart, motifs }]);

  /** Removes the whole person the order belongs to. */
  const removeByCart = (cartId: number) =>
    update(contacts.filter((c) => !c.cartIds.includes(cartId)));

  const remove = (contact: IReservationContact) =>
    update(contacts.filter((c) => c !== contact));

  const clear = () => update([]);

  return { contacts, isInList, add, addMany, removeByCart, remove, clear };
};
